import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import AuthGuard from "../components/AuthGuard";
import EmptyState from "../components/EmptyState";
import SkeletonCard from "../components/SkeletonCard";
import { api } from "../api/client";
import { Package, ChevronRight, ShieldAlert } from "lucide-react";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const STATUS_COPY = {
    requested: { label: "Awaiting review", tone: "bg-amber-100 text-amber-800" },
    approved: { label: "Approved", tone: "bg-blue-100 text-blue-700" },
    pickup_scheduled: { label: "Pickup scheduled", tone: "bg-blue-100 text-blue-700" },
    picked_up: { label: "Picked up", tone: "bg-violet-100 text-violet-700" },
    inspected: { label: "Inspected", tone: "bg-violet-100 text-violet-700" },
    refunded: { label: "Refunded", tone: "bg-emerald-100 text-emerald-700" },
    rejected: { label: "Rejected", tone: "bg-rose-100 text-rose-700" },
    cancelled: { label: "Cancelled", tone: "bg-zinc-100 text-zinc-700" },
};

const Returns = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        api.get("/returns").then((r) => setItems(r.data.items || [])).finally(() => setLoading(false));
    }, []);
    return (
        <AuthGuard>
            <div className="min-h-screen bg-white dark:bg-[#0B0B12]">
                <Navbar />
                <main className="mx-auto max-w-5xl px-6 py-10 lg:px-12">
                    <div className="mb-8 flex items-end justify-between gap-3">
                        <div>
                            <p className="label-uppercase text-zinc-500">Returns &amp; refunds</p>
                            <h1 className="text-3xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50" style={{ letterSpacing: "-0.01em" }}>Your returns</h1>
                            <p className="mt-1 text-sm text-zinc-500">Within 14 days of delivery you can request a return. Refunds go back to your card or wallet.</p>
                        </div>
                        <Link to="/orders" className="text-sm font-medium text-brand-700 hover:underline">View orders →</Link>
                    </div>

                    {loading ? (
                        <div className="grid gap-4 sm:grid-cols-2">{Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)}</div>
                    ) : items.length === 0 ? (
                        <EmptyState
                            title="No returns yet"
                            description="When you receive a delivered order, you'll be able to request a return from the order detail page."
                            icon={<Package className="h-8 w-8" />}
                            action={<Link to="/orders" className="btn-brand">Go to orders</Link>}
                        />
                    ) : (
                        <ul className="grid gap-3" data-testid="returns-list">
                            {items.map((r) => (
                                <li key={r.id}>
                                    <Link
                                        to={`/returns/${r.id}`}
                                        className="flex items-center justify-between gap-4 rounded-2xl border border-zinc-100 bg-white p-5 shadow-sm transition-colors hover:border-brand-200 dark:bg-white/[0.03]"
                                        data-testid={`return-row-${r.id}`}
                                    >
                                        <div className="min-w-0 flex-1">
                                            <p className="font-medium text-zinc-900 dark:text-zinc-50">Return for order {r.order_no || r.order_id?.slice(0, 8)}</p>
                                            <p className="mt-1 text-xs text-zinc-500">{r.items?.length || 0} item(s) • {inr(r.requested_refund)}</p>
                                            {r.ai_validation?.flagged && (
                                                <p className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-amber-700"><ShieldAlert className="h-3 w-3" /> Flagged for manual review</p>
                                            )}
                                        </div>
                                        <span className={`whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_COPY[r.status]?.tone || "bg-zinc-100 text-zinc-700"}`}>
                                            {STATUS_COPY[r.status]?.label || r.status}
                                        </span>
                                        <ChevronRight className="h-4 w-4 text-zinc-400" />
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </main>
                <Footer />
            </div>
        </AuthGuard>
    );
};

export default Returns;
